import { useEffect } from 'react';
import { startupDataService } from '@ideoz/core';
import { appDataStore, type LiveEditResult, type LiveRecordEdit } from '@ideoz/workbench';
import i18n from '../../i18n';
import { typedValue } from './cells';

/** The preview token lasts 12 hours (Nest `startup-data.service.ts`); ask again well before. */
const REFRESH_MS = 10 * 60 * 60 * 1000;

/**
 * Connects the workbench to the startup's live data (User Story 596): gets the founder's preview
 * token the prototype in the preview asks for, and saves hot edits of live rows through the
 * founder's routes. Without a startup the preview keeps its sample rows.
 */
export function useWorkbenchAppData(projectId: string | null | undefined): void {
  useEffect(() => {
    if (!projectId) {
      appDataStore.config.set(null);
      appDataStore.liveEdit.set(null);
      return;
    }
    let cancelled = false;
    const fetchToken = async () => {
      try {
        const config = await startupDataService.previewSession(projectId);
        if (!cancelled) appDataStore.config.set(config);
      } catch {
        // No live data in the preview: it keeps its sample rows, as before.
        if (!cancelled) appDataStore.config.set(null);
      }
    };
    appDataStore.config.set(undefined);
    void fetchToken();
    const timer = setInterval(() => void fetchToken(), REFRESH_MS);
    appDataStore.liveEdit.set((edit) => saveLiveEdit(projectId, edit));

    return () => {
      cancelled = true;
      clearInterval(timer);
      appDataStore.liveEdit.set(null);
      appDataStore.config.set(undefined);
    };
  }, [projectId]);
}

/**
 * A hot edit of a row that is live data: hot edit knows the record id and the field, not the
 * entity, so the entity that holds the id is looked up, and the value keeps the field's type.
 */
export async function saveLiveEdit(
  projectId: string,
  edit: LiveRecordEdit
): Promise<LiveEditResult> {
  try {
    const { entities } = await startupDataService.overview(projectId);
    for (const entity of entities) {
      if (entity.records === 0) continue;
      const rows = await startupDataService.records(projectId, entity.entity);
      const row = rows.find((r) => r.record.id === edit.recordId);
      if (!row) continue;
      const field = entity.fields.find((f) => f.name === edit.field);
      const typed = typedValue(edit.newText, row.record[edit.field], field);
      if ('error' in typed) {
        const key =
          typed.error === 'number' ? 'startupData.fieldNumber' : 'startupData.fieldBoolean';
        return { ok: false, error: i18n.t(key, { field: edit.field }) };
      }
      await startupDataService.save(
        projectId,
        entity.entity,
        { [edit.field]: typed.value },
        edit.recordId
      );
      return { ok: true, entity: entity.entity };
    }
    return { ok: false, error: i18n.t('startupData.liveEditMissing') };
  } catch (error) {
    return {
      ok: false,
      error: error instanceof Error ? error.message : i18n.t('startupData.liveEditFailed'),
    };
  }
}
